import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import { format, subDays, subMonths } from 'date-fns';
import { Box, ChipButton, CloseButton, Popover, SegmentControl, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { SpecificRangePicker } from './SpecificRangePicker';
import styles from './TimePeriodSelector.module.scss';
export const GetTimePeriodSelectorTID = (parentTestId)=>getTestIds(parentTestId);
const formatDateShort = (timestamp)=>format(new Date(timestamp), 'MMM d, yyyy');
const initialDraftRange = (value, now)=>value.preset === 'custom' ? {
        from: value.fromDate,
        to: value.toDate
    } : {
        from: subMonths(now, 1).getTime(),
        to: now.getTime()
    };
export const TimePeriodSelector = (param)=>{
    let { value, now, onChange, testId } = param;
    const { TimePeriodSelectorTestIds } = useMemo(()=>GetTimePeriodSelectorTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys('components.invoiceMetricsSection');
    const { isMobileDevice } = useDevice();
    const chipRef = useRef(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const segmentOptions = useMemo(()=>[
            {
                value: 'last-7-days',
                label: texts.timePeriodPresets.sevenDays(),
                testId: TimePeriodSelectorTestIds.Segment('last-7-days')
            },
            {
                value: 'last-1-month',
                label: texts.timePeriodPresets.thirtyDays(),
                testId: TimePeriodSelectorTestIds.Segment('last-1-month')
            },
            {
                value: 'last-3-months',
                label: texts.timePeriodPresets.lastThreeMonths(),
                testId: TimePeriodSelectorTestIds.Segment('last-3-months')
            },
            {
                value: 'all-time',
                label: texts.timePeriodPresets.allTime(),
                testId: TimePeriodSelectorTestIds.Segment('all-time')
            }
        ], [
        TimePeriodSelectorTestIds,
        texts
    ]);
    const segmentValue = value.preset === 'custom' ? '' : value.preset;
    const handleSegmentChange = (preset)=>{
        if (value.preset !== 'custom' && preset === value.preset) return;
        onChange({
            preset
        });
    };
    const handleApplyCustomRange = (fromMs, toMs)=>{
        onChange({
            preset: 'custom',
            fromDate: fromMs,
            toDate: toMs
        });
        setPopoverOpen(false);
    };
    const clearCustomRange = (e)=>{
        e.stopPropagation();
        onChange({
            preset: 'last-1-month'
        });
    };
    const specificRangeLabel = value.preset === 'custom' ? "".concat(formatDateShort(value.fromDate), " – ").concat(formatDateShort(value.toDate)) : texts.timePeriodPresets.specificRange();
    const initialDraft = initialDraftRange(value, now);
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.root,
        testId: TimePeriodSelectorTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(SegmentControl, {
                testId: TimePeriodSelectorTestIds.SegmentControl(),
                className: styles.segmentControl,
                options: segmentOptions,
                value: segmentValue,
                onChange: handleSegmentChange,
                bi: {
                    name: 'time_period'
                }
            }),
            /*#__PURE__*/ _jsx(ChipButton, {
                ref: chipRef,
                bi: {
                    name: 'specific_time_range'
                },
                testId: TimePeriodSelectorTestIds.SpecificRangeChip(),
                className: styles.specificRangeChip,
                highlight: value.preset === 'custom',
                onClick: ()=>setPopoverOpen(true),
                suffixIcon: value.preset === 'custom' ? /*#__PURE__*/ _jsx(CloseButton, {
                    bi: {
                        name: 'clear_custom_time_period'
                    },
                    testId: TimePeriodSelectorTestIds.SpecificRangeClearButton(),
                    size: "xsmall",
                    bordered: true,
                    onClick: clearCustomRange
                }) : undefined,
                children: specificRangeLabel
            }),
            /*#__PURE__*/ _jsx(Popover, {
                open: popoverOpen,
                anchorEl: chipRef.current,
                onClose: ()=>setPopoverOpen(false),
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                },
                children: popoverOpen && /*#__PURE__*/ _jsx(SpecificRangePicker, {
                    initialFrom: initialDraft.from,
                    initialTo: initialDraft.to,
                    now: now,
                    isMobileDevice: isMobileDevice,
                    fromPickerTestId: TimePeriodSelectorTestIds.CustomFromPicker(),
                    toPickerTestId: TimePeriodSelectorTestIds.CustomToPicker(),
                    applyButtonTestId: TimePeriodSelectorTestIds.ApplyButton(),
                    onApply: handleApplyCustomRange
                })
            })
        ]
    });
};
const localCalendarDayUtcMs = (localMs)=>{
    const d = new Date(localMs);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
};
export function getDateRangeForTimePeriod(value, now) {
    switch(value.preset){
        case 'last-7-days':
            return {
                issuedOnSince: localCalendarDayUtcMs(subDays(now, 7).getTime()),
                issuedOnUntil: localCalendarDayUtcMs(now.getTime())
            };
        case 'last-1-month':
            return {
                issuedOnSince: localCalendarDayUtcMs(subMonths(now, 1).getTime()),
                issuedOnUntil: localCalendarDayUtcMs(now.getTime())
            };
        case 'last-3-months':
            return {
                issuedOnSince: localCalendarDayUtcMs(subMonths(now, 3).getTime()),
                issuedOnUntil: localCalendarDayUtcMs(now.getTime())
            };
        case 'all-time':
            return {
                issuedOnSince: null,
                issuedOnUntil: null
            };
        case 'custom':
            return {
                issuedOnSince: localCalendarDayUtcMs(value.fromDate),
                issuedOnUntil: localCalendarDayUtcMs(value.toDate)
            };
    }
}
export const DEFAULT_TIME_PERIOD = {
    preset: 'last-1-month'
};
const SEGMENT_PRESETS = [
    'last-7-days',
    'last-1-month',
    'last-3-months',
    'all-time'
];
export function serializeTimePeriod(value) {
    if (value.preset === 'custom') {
        return "custom:".concat(value.fromDate, ":").concat(value.toDate);
    }
    return value.preset;
}
export function parseTimePeriod(raw) {
    if (raw == null || raw === '') return DEFAULT_TIME_PERIOD;
    if (raw.startsWith('custom:')) {
        const [, fromStr, toStr] = raw.split(':');
        const fromDate = Number(fromStr);
        const toDate = Number(toStr);
        if (Number.isFinite(fromDate) && Number.isFinite(toDate) && fromDate > 0 && toDate > 0) {
            return {
                preset: 'custom',
                fromDate,
                toDate
            };
        }
        return DEFAULT_TIME_PERIOD;
    }
    return SEGMENT_PRESETS.includes(raw) ? {
        preset: raw
    } : DEFAULT_TIME_PERIOD;
}
export function migrateLegacyDateParam(legacyDate) {
    if (legacyDate == null || legacyDate === '') return DEFAULT_TIME_PERIOD;
    switch(legacyDate){
        case 'all-time':
            return {
                preset: 'all-time'
            };
        case 'last-three-months':
            return {
                preset: 'last-3-months'
            };
        case 'today':
        case 'this-week':
        case 'this-month':
        case 'previous-month':
            return {
                preset: 'last-1-month'
            };
        default:
            return DEFAULT_TIME_PERIOD;
    }
}
